import React from 'react';
import { useTranslation } from 'react-i18next';
import { DictationInputMode } from '../../types/auth';
import './InputModeModal.css';

export type { DictationInputMode };

interface InputModeModalProps {
  /** Recommended mode based on CEFR level */
  recommended: DictationInputMode;
  /** User's estimated CEFR level */
  cefrLevel: string;
  onSelect: (mode: DictationInputMode) => void;
}

const InputModeModal: React.FC<InputModeModalProps> = ({
  recommended,
  cefrLevel,
  onSelect,
}) => {
  const { t } = useTranslation('placementTest');
  const isBeginnerRecommend = recommended === 'letter-picker';

  return (
    <div className="imm-overlay" onClick={(e) => e.stopPropagation()}>
      <div className="imm-modal">
        <div className="imm-header">
          <svg className="imm-icon" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <rect x="2" y="6" width="20" height="12" rx="2" />
            <line x1="6" y1="10" x2="6" y2="10" /><line x1="10" y1="10" x2="10" y2="10" /><line x1="14" y1="10" x2="14" y2="10" /><line x1="18" y1="10" x2="18" y2="10" />
            <line x1="8" y1="14" x2="16" y2="14" />
          </svg>
          <h3 className="imm-title">{t('inputMode.title')}</h3>
          <p className="imm-subtitle">
            {t('inputMode.subtitle', { level: cefrLevel })}
          </p>
        </div>

        <div className="imm-options">
          {/* Letter Picker option */}
          <button
            className={`imm-option ${recommended === 'letter-picker' ? 'imm-option--recommended' : ''}`}
            onClick={() => onSelect('letter-picker')}
          >
            {recommended === 'letter-picker' && (
              <span className="imm-badge">{t('inputMode.recommended')}</span>
            )}
            <div className="imm-option-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="4" width="7" height="7" rx="1.5" /><rect x="14" y="4" width="7" height="7" rx="1.5" />
                <rect x="3" y="13" width="7" height="7" rx="1.5" /><rect x="14" y="13" width="7" height="7" rx="1.5" />
                <text x="6.5" y="9.5" textAnchor="middle" fontSize="6" fill="currentColor" stroke="none" fontWeight="600">a</text>
                <text x="17.5" y="9.5" textAnchor="middle" fontSize="6" fill="currentColor" stroke="none" fontWeight="600">b</text>
                <text x="6.5" y="18.5" textAnchor="middle" fontSize="6" fill="currentColor" stroke="none" fontWeight="600">c</text>
                <text x="17.5" y="18.5" textAnchor="middle" fontSize="6" fill="currentColor" stroke="none" fontWeight="600">d</text>
              </svg>
            </div>
            <div className="imm-option-content">
              <h4>{t('inputMode.letterPicker.title')}</h4>
              <p>{t('inputMode.letterPicker.desc')}</p>
            </div>
            <div className="imm-option-preview">
              <span className="imm-preview-btn">b</span>
              <span className="imm-preview-btn imm-preview-btn--active">p</span>
              <span className="imm-preview-btn">d</span>
              <span className="imm-preview-btn">t</span>
            </div>
          </button>

          {/* Keyboard option */}
          <button
            className={`imm-option ${recommended === 'keyboard' ? 'imm-option--recommended' : ''}`}
            onClick={() => onSelect('keyboard')}
          >
            {recommended === 'keyboard' && (
              <span className="imm-badge">{t('inputMode.recommended')}</span>
            )}
            <div className="imm-option-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="6" width="20" height="12" rx="2" />
                <line x1="6" y1="10" x2="6" y2="10" /><line x1="10" y1="10" x2="10" y2="10" /><line x1="14" y1="10" x2="14" y2="10" /><line x1="18" y1="10" x2="18" y2="10" />
                <line x1="8" y1="14" x2="16" y2="14" />
              </svg>
            </div>
            <div className="imm-option-content">
              <h4>{t('inputMode.keyboard.title')}</h4>
              <p>{t('inputMode.keyboard.desc')}</p>
            </div>
            <div className="imm-option-preview imm-preview-keyboard">
              <div className="imm-kb-row">
                <span>q</span><span>w</span><span>e</span><span>r</span><span>t</span>
              </div>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
};

export default InputModeModal;
